import { z } from 'zod';
import { API_BASE, apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * INSTALOWANIE PROGRAMÓW NA MASZYNACH — biblioteka paczek i zlecanie instalacji.
 *
 * ⚠️ POWSTAŁO Z AWARII (2026-08-24): agent NinjaOne zniknął ze WSZYSTKICH maszyn vendingowych,
 * a nasz agent stoi na każdej. Funkcja jest CELOWO OGÓLNA — służy tak samo do 7-zipa czy Notepada++,
 * a nic o żadnym konkretnym programie nie siedzi w kodzie.
 */

export const PakietSchema = z.object({
  id: z.string(),
  nazwa: z.string(),
  plik: z.string(),
  rozmiar: z.number(),
  sha256: z.string(),
  parametry: z.string().nullable(),
  wgralEmail: z.string().nullable(),
  createdAt: z.string(),
});

export type Pakiet = z.infer<typeof PakietSchema>;

export const PakietySchema = z.object({ pakiety: z.array(PakietSchema) });

export const fetchPakiety = (groupId: string) =>
  apiGet(`/programy/pakiety?groupId=${encodeURIComponent(groupId)}`, PakietySchema);

export const usunPakiet = (groupId: string, id: string) =>
  apiSend('DELETE', `/programy/pakiety/${id}?groupId=${encodeURIComponent(groupId)}`);

export const ZlecenieSchema = z.object({
  id: z.string(),
  opis: z.string(),
  wymagajPodpisu: z.boolean(),
});

export type Zlecenie = {
  groupId: string;
  agentId: string;
  pakietId?: string;
  url?: string;
  nazwaPliku?: string;
  sha256?: string;
  parametry?: string;
  kodySukcesu?: number[];
  limitSekund?: number;
  wymagajPodpisu?: boolean;
};

export const zlecInstalacje = (dane: Zlecenie) => apiPost('/programy/instaluj', dane, ZlecenieSchema);

export const WgranyPakietSchema = PakietSchema.extend({ jużByła: z.boolean().optional() });

/**
 * WGRANIE PACZKI — ciałem jest sam plik, bez `FormData`.
 *
 * ⚠️ `XMLHttpRequest`, A NIE `fetch`, i to jedyny powód: postęp wysyłki. Instalator waży dziesiątki
 * megabajtów, a wysyłka bez paska wygląda jak zawieszony ekran. Ta sama decyzja i ten sam kształt
 * co przy wgrywaniu kopii bazy (`agents-api.ts`).
 *
 * ⚠️ `Content-Type` MUSI być jednym z tych, które zna parser surowego ciała w API — inaczej Fastify
 * odrzuca żądanie `415` po przesłaniu całej paczki.
 */
export function wgrajPakiet(
  groupId: string,
  plik: File,
  opis: { nazwa: string; parametry: string },
  postep?: (procent: number) => void,
): Promise<z.infer<typeof WgranyPakietSchema>> {
  return new Promise((rozwiaz, odrzuc) => {
    const xhr = new XMLHttpRequest();
    const adres =
      `${API_BASE}/programy/pakiet?groupId=${encodeURIComponent(groupId)}` +
      `&plik=${encodeURIComponent(plik.name)}` +
      `&nazwa=${encodeURIComponent(opis.nazwa)}` +
      (opis.parametry.trim() === '' ? '' : `&parametry=${encodeURIComponent(opis.parametry)}`);

    xhr.open('PUT', adres);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && postep !== undefined) postep(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      const tresc: unknown = ((): unknown => {
        try {
          return JSON.parse(xhr.responseText) as unknown;
        } catch {
          return null;
        }
      })();
      if (xhr.status >= 200 && xhr.status < 300) {
        const w = WgranyPakietSchema.safeParse(tresc);
        w.success ? rozwiaz(w.data) : odrzuc(new Error('Instancja przyjęła paczkę, ale odpowiedź jest nie ta'));
        return;
      }
      const komunikat =
        tresc !== null && typeof tresc === 'object' && 'message' in tresc
          ? String((tresc as { message: unknown }).message)
          : `Wgranie nie udało się (HTTP ${xhr.status})`;
      odrzuc(new Error(komunikat));
    };
    xhr.onerror = () => odrzuc(new Error('Wysyłka przerwana — sprawdź łącze i spróbuj ponownie'));
    xhr.send(plik);
  });
}
